import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Circle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { ensureProjectMapMarkerIcon, MAP_TILE_ATTRIBUTION, MAP_TILE_MAX_ZOOM, MAP_TILE_URL } from "./projectMapLeaflet";
import "./ProjectMapPreview.css";

function FitPreview({ lat, lng, radiusMiles }: { lat: number; lng: number; radiusMiles: number }) {
    const map = useMap();
    useEffect(() => {
        const bounds = L.latLngBounds([[lat, lng]]);
        const milesPerDegLat = 69;
        const milesPerDegLng = 69 * Math.cos((lat * Math.PI) / 180);
        const dLat = radiusMiles / milesPerDegLat;
        const dLng = radiusMiles / milesPerDegLng;
        bounds.extend([lat + dLat, lng + dLng]);
        bounds.extend([lat - dLat, lng - dLng]);
        map.fitBounds(bounds.pad(0.12), { maxZoom: 15 });
    }, [map, lat, lng, radiusMiles]);
    return null;
}

export interface ProjectMapPreviewProps {
    latitude: number;
    longitude: number;
    radiusMiles: number;
    onOpen: () => void;
}

const ProjectMapPreview: React.FC<ProjectMapPreviewProps> = ({
    latitude,
    longitude,
    radiusMiles,
    onOpen,
}) => {
    ensureProjectMapMarkerIcon();

    const showCircle = radiusMiles > 0;

    return (
        <button
            type="button"
            className="project-map-preview"
            onClick={onOpen}
            aria-label="Open project map"
        >
            <div className="project-map-preview-inner">
                <MapContainer
                    center={[latitude, longitude]}
                    zoom={14}
                    className="project-map-preview-map"
                    scrollWheelZoom={false}
                    dragging={false}
                    zoomControl={false}
                    doubleClickZoom={false}
                    boxZoom={false}
                    keyboard={false}
                    attributionControl={true}
                >
                    <TileLayer
                        attribution={MAP_TILE_ATTRIBUTION}
                        url={MAP_TILE_URL}
                        maxZoom={MAP_TILE_MAX_ZOOM}
                        referrerPolicy="strict-origin-when-cross-origin"
                    />
                    <FitPreview lat={latitude} lng={longitude} radiusMiles={showCircle ? radiusMiles : 0.25} />
                    {showCircle && (
                        <Circle
                            center={[latitude, longitude]}
                            radius={radiusMiles * 1609.344}
                            pathOptions={{
                                color: "#3388ff",
                                fillColor: "#3388ff",
                                fillOpacity: 0.15,
                                weight: 2,
                            }}
                        />
                    )}
                    <Marker position={[latitude, longitude]} />
                </MapContainer>
            </div>
            <span className="project-map-preview-hint">Click to open map</span>
        </button>
    );
};

export default ProjectMapPreview;
